import { useState, useEffect } from 'react';
import { facilitadoresApi, type Facilitador } from '../services/programService';

const ROLES = ['consultor', 'coordenador', 'facilitador', 'assistente'];
const CORES = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#ef4444', '#06b6d4'];

export default function FacilitadoresPage() {
  const [list, setList] = useState<Facilitador[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState<Facilitador | null>(null);
  const [form, setForm] = useState({ nome: '', role: 'consultor', email: '', whatsapp: '', especialidade: '', cor_avatar: '#f59e0b' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setList(await facilitadoresApi.list()); } finally { setLoading(false); }
  };

  const openNew = () => { setEdit(null); setForm({ nome: '', role: 'consultor', email: '', whatsapp: '', especialidade: '', cor_avatar: '#f59e0b' }); setShow(true); };
  const openEdit = (f: Facilitador) => { setEdit(f); setForm({ nome: f.nome, role: f.role, email: f.email || '', whatsapp: f.whatsapp || '', especialidade: f.especialidade || '', cor_avatar: f.cor_avatar }); setShow(true); };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (edit) await facilitadoresApi.update(edit.id, form);
      else await facilitadoresApi.create(form);
      setShow(false); load();
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Desativar este facilitador?')) return;
    await facilitadoresApi.delete(id);
    load();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-black text-gray-900">Facilitadores</h1>
        <button onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-gray-950 font-bold text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer">
          + Novo Facilitador
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : list.length === 0 ? (
        <div className="text-center py-12 text-gray-400">Nenhum facilitador cadastrado.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(f => (
            <div key={f.id} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ background: f.cor_avatar }}>
                  {f.nome.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 truncate">{f.nome}</h3>
                  <span className="text-xs text-gray-500 capitalize">{f.role}</span>
                  {f.especialidade && <p className="text-xs text-gray-400 mt-0.5 truncate">{f.especialidade}</p>}
                </div>
              </div>
              {f.email && <p className="text-sm text-gray-500 truncate mb-1"><i className="ri-mail-line mr-1" />{f.email}</p>}
              {f.whatsapp && <p className="text-sm text-gray-500 truncate mb-3"><i className="ri-whatsapp-line mr-1" />{f.whatsapp}</p>}
              <div className="flex gap-2">
                <button onClick={() => openEdit(f)} className="flex-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg transition-colors cursor-pointer">Editar</button>
                <button onClick={() => handleDelete(f.id)} className="flex-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 py-2 rounded-lg transition-colors cursor-pointer">Remover</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-black text-gray-900 mb-4">{edit ? 'Editar' : 'Novo'} Facilitador</h2>
            <form onSubmit={handleSave} className="space-y-3">
              <input required placeholder="Nome completo" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm" />
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm">
                {ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
              </select>
              <input type="email" placeholder="E-mail" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm" />
              <input placeholder="WhatsApp" value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm" />
              <input placeholder="Especialidade" value={form.especialidade} onChange={e => setForm({ ...form, especialidade: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm" />
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Cor do avatar</label>
                <div className="flex gap-2">
                  {CORES.map(c => (
                    <button key={c} type="button" onClick={() => setForm({ ...form, cor_avatar: c })} className="w-8 h-8 rounded-full border-2 transition-all cursor-pointer" style={{ background: c, borderColor: form.cor_avatar === c ? '#000' : 'transparent' }} />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShow(false)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-950 font-bold py-2.5 rounded-xl text-sm transition-colors cursor-pointer">
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
